import React from 'react';
import { DropResult } from 'react-beautiful-dnd';

import {
  CmBox,
  CmFlex,
  CmFormErrorMessage,
  CmFormLabel,
  CmLabelAndHorizonInput,
  CmLabelAndInput,
  CmLabelAndTextarea,
  CmList,
  CmListItem,
  CmStack,
} from '@/components/uiParts';
import {
  MenuManagementDragItems,
  MenuManagementSearchAndDragArea,
} from '@/pages/menu-managements/components/SearchAndDrag';
import { MenuManagementCardWrapper } from '@/pages/menu-managements/components/Wrapper';
import {
  OptionGroupDelivery,
  OptionGroupEatIn,
  optionGroupForm,
  OptionGroupTakeout,
  Quantity,
} from '@/pages/menu-managements/option-groups/service';
import { OptionGroupFormError } from '@/pages/menu-managements/option-groups/validate';
import { SearchItem } from '@/pages/menu-managements/services/searchItem';

type Props = {
  titleJa: string;
  memo: string;
  takeout: OptionGroupTakeout;
  eatIn: OptionGroupEatIn;
  delivery: OptionGroupDelivery;
  error: OptionGroupFormError;
  searchItems: SearchItem[];
  selectedItems: SearchItem[];
  searchItemName: string;
  brandSelectValue: string;
  isSearching: boolean;
  quantity: Quantity;
  handleInputTitleJa: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputMemo: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleInputTakeoutPosCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputEatInPosCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputDeliveryPosCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleOnDragEnd: (result: DropResult) => void;
  handleSearchItemNameInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddSelectItems: (item: SearchItem) => void;
  handleDeleteDragItem: (itemId: string) => void;
  handleInputQuantityMin: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputQuantityMax: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputQuantityMaxSameOption: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleClickMultipleSelect: () => void;
};

export const MenuManagementOptionGroupsForm: React.FC<Props> = ({
  titleJa,
  memo,
  error,
  takeout,
  eatIn,
  delivery,
  searchItems,
  selectedItems,
  searchItemName,
  brandSelectValue,
  isSearching,
  quantity,
  handleInputTitleJa,
  handleInputMemo,
  handleInputTakeoutPosCode,
  handleInputEatInPosCode,
  handleInputDeliveryPosCode,
  handleOnDragEnd,
  handleSearchItemNameInput,
  handleAddSelectItems,
  handleDeleteDragItem,
  handleInputQuantityMin,
  handleInputQuantityMax,
  handleInputQuantityMaxSameOption,
  handleClickMultipleSelect,
}): JSX.Element => {
  return (
    <MenuManagementCardWrapper>
      <CmStack spacing="20px">
        <CmLabelAndInput
          isRequired
          id={optionGroupForm.title.key}
          label={optionGroupForm.title.label}
          isInvalid={error.titleJa.isError}
          fontWeight="bold"
          value={titleJa}
          onChange={handleInputTitleJa}
        >
          <CmFormErrorMessage>{error.titleJa.message}</CmFormErrorMessage>
        </CmLabelAndInput>

        <CmLabelAndTextarea
          id={optionGroupForm.memo.key}
          label={optionGroupForm.memo.label}
          isInvalid={error.memo.isError}
          fontWeight="bold"
          error={<CmFormErrorMessage>{error.memo.message}</CmFormErrorMessage>}
          value={memo}
          onChange={handleInputMemo}
        />
        <CmLabelAndInput
          id={optionGroupForm.takeout.pos_code.key}
          label={optionGroupForm.takeout.pos_code.label}
          fontWeight="bold"
          value={takeout.pos_code}
          onChange={handleInputTakeoutPosCode}
        />
        <CmLabelAndInput
          id={optionGroupForm.eatIn.pos_code.key}
          label={optionGroupForm.eatIn.pos_code.label}
          fontWeight="bold"
          value={eatIn.pos_code}
          onChange={handleInputEatInPosCode}
        />
        <CmLabelAndInput
          id={optionGroupForm.delivery.pos_code.key}
          label={optionGroupForm.delivery.pos_code.label}
          fontWeight="bold"
          value={delivery.pos_code}
          onChange={handleInputDeliveryPosCode}
        />
        <MenuManagementSearchAndDragArea
          targetName="オプション商品"
          {...{
            brandSelectValue,
            searchItems,
            isSearching,
            selectedItems,
            searchItemName,
            handleSearchItemNameInput,
            handleAddSelectItems,
            handleClickMultipleSelect,
          }}
        >
          <MenuManagementDragItems
            targetOption="items"
            {...{ selectedItems, handleOnDragEnd, handleDeleteDragItem }}
          />
        </MenuManagementSearchAndDragArea>

        <CmBox>
          <CmFlex justifyContent={'space-between'}>
            <CmFormLabel fontWeight="bold">
              {optionGroupForm.optionGroupItemNum.label}
            </CmFormLabel>
          </CmFlex>

          <CmList pl="20px">
            <CmListItem display="flex" alignItems="center">
              <CmLabelAndHorizonInput
                labelWidth="350px"
                id={optionGroupForm.min.key}
                label={optionGroupForm.min.label}
                isRequired
                isInvalid={error.quantity.min.isError}
                value={quantity.min}
                fontWeight="bold"
                inputWidth="100px"
                onChange={handleInputQuantityMin}
              >
                <CmFormErrorMessage>
                  {error.quantity.min.message}
                </CmFormErrorMessage>
              </CmLabelAndHorizonInput>
            </CmListItem>
            <CmListItem mt="10px" display="flex" alignItems="center">
              <CmLabelAndHorizonInput
                labelWidth="350px"
                id={optionGroupForm.max.key}
                label={optionGroupForm.max.label}
                isInvalid={error.quantity.max.isError}
                fontWeight="bold"
                inputWidth="100px"
                value={quantity.max}
                onChange={handleInputQuantityMax}
              >
                <CmFormErrorMessage>
                  {error.quantity.max.message}
                </CmFormErrorMessage>
              </CmLabelAndHorizonInput>
            </CmListItem>

            <CmListItem mt="10px" display="flex" alignItems="center">
              <CmLabelAndHorizonInput
                labelWidth="350px"
                id={optionGroupForm.maxSameOption.key}
                label={optionGroupForm.maxSameOption.label}
                isInvalid={error.quantity.maxSameOption.isError}
                fontWeight="bold"
                inputWidth="100px"
                value={quantity.max_same_option}
                onChange={handleInputQuantityMaxSameOption}
              >
                <CmFormErrorMessage>
                  {error.quantity.maxSameOption.message}
                </CmFormErrorMessage>
              </CmLabelAndHorizonInput>
            </CmListItem>
          </CmList>
        </CmBox>
      </CmStack>
    </MenuManagementCardWrapper>
  );
};
